// My Work: one place for everything that needs the person, from every app. Laid out like every list page in
// courageMFG (the service desk, work orders): views on the left with their counts, a filter, and data grids, grouped
// by section (Waiting on you, Ready for you, In progress, Done this week), then by area, then by app.
//
//   Waiting on you   approvals, signatures and decisions; "Asked of you" when the record names you, otherwise anyone
//                    allowed to approve in that app can take it
//   Ready for you    next steps Courage found in the records and drafted (the same feed as Marketing's, filtered to you);
//                    the Ready for you view is where they're approved, edited or skipped
//   In progress      what you own; Overdue is the part of it that's late
//   Done this week   what you approved, signed, closed or finished in the last seven days
//
// Each row opens the record, where the work is done with everything in front of you.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLoad } from "../hooks";
import { ErrorBox, label } from "../ui";
import { SUITE } from "../suite";
import { useAccess } from "../security/store";
import { ReadyForYou } from "../marketing/Ready";
import type { Move } from "../marketing/movesApi";
import { myWork, appNameOf, areaOf, areaName, workChanged, type WorkItem } from "./myWorkApi";
import "../marketing/mk.css";
import "./mywork.css";

type Section = "waiting" | "ready" | "progress" | "done";
type View = "all" | Section | "asked" | "overdue";
const VIEWS: { id: View; name: string; sub?: boolean }[] = [
  { id: "all", name: "All my work" },
  { id: "waiting", name: "Waiting on you" },
  { id: "asked", name: "Asked of you", sub: true },
  { id: "ready", name: "Ready for you" },
  { id: "progress", name: "In progress" },
  { id: "overdue", name: "Overdue", sub: true },
  { id: "done", name: "Done this week" },
];
const SECTION_NAME: Record<Section, string> = { waiting: "Waiting on you", ready: "Ready for you", progress: "In progress", done: "Done this week" };

/** Today on the person's own calendar (due dates are calendar dates, not instants). */
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const dateText = (iso: string) => new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
/** How long it has been waiting: "23 d", "5 h", "< 1 h". */
function waited(at: string) {
  const h = (Date.now() - new Date(at).getTime()) / 36e5;
  return h < 1 ? "< 1 h" : h < 24 ? `${Math.floor(h)} h` : `${Math.floor(h / 24)} d`;
}
function when(at: string) {
  const d = new Date(at), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
const firstSentence = (s: string) => s.split(/(?<=\.)\s/)[0];

/** One row of a section's grid. */
type Line = { key: string; number: string | null; href: string; title: string; sub: string | null; area: string; appName: string; cells: ReactNode[]; due?: string | null; asked?: boolean };
type Col = { label: string; hideM?: boolean };

const dueCell = (due: string | null | undefined) => !due ? <span className="muted">–</span>
  : <span className={due < todayIso() ? "mw-late" : ""}>{dateText(due)}</span>;

const COLS: Record<Section, Col[]> = {
  waiting: [{ label: "To do" }, { label: "Waiting on", hideM: true }, { label: "Waiting" }, { label: "Due", hideM: true }],
  ready: [{ label: "To do" }, { label: "For", hideM: true }, { label: "When" }],
  progress: [{ label: "Status" }, { label: "Due" }, { label: "Updated", hideM: true }],
  done: [{ label: "What you did" }, { label: "When" }],
};

function itemLine(w: WorkItem, s: Exclude<Section, "ready">): Line {
  const base = { key: w.key, number: w.number, href: w.href, title: w.title, area: areaOf(w), appName: appNameOf(w), due: w.due, asked: w.asked };
  if (s === "waiting") return { ...base, sub: w.detail, cells: [
    <Link to={w.href}>{w.action}</Link>,
    w.asked ? <b>You</b> : <span className="muted">Any approver</span>,
    <span className="muted" title={new Date(w.at).toLocaleString()}>{waited(w.at)}</span>,
    dueCell(w.due),
  ] };
  if (s === "progress") return { ...base, sub: [w.action && `Next: ${w.action}`, w.detail].filter(Boolean).join(" · ") || null, cells: [
    w.state ? <span className="mw-chip">{label(w.state)}</span> : <span className="muted">–</span>,
    dueCell(w.due),
    <span className="muted">{when(w.at)}</span>,
  ] };
  return { ...base, sub: w.detail, cells: [<span className="mw-chip ok">{w.action}</span>, <span className="muted">{when(w.at)}</span>] };
}

/** A drafted step, as a row: it opens in the Ready for you view, where it's approved, edited or skipped. */
const MOVE_APP: Record<string, { appName: string; area: string }> = {
  reply: { appName: "Leads", area: "marketing" }, follow_up: { appName: "Leads", area: "marketing" }, event_follow_up: { appName: "Leads", area: "marketing" },
  win_back: { appName: "Accounts", area: "commercial" }, event_push: { appName: "Content calendar", area: "marketing" }, reuse: { appName: "Content calendar", area: "marketing" },
};
function moveLine(m: Move, meId: string | null): Line {
  const a = MOVE_APP[m.kind] ?? { appName: "Marketing", area: "marketing" };
  const href = `/my-work?v=ready&k=${encodeURIComponent(m.key)}`;
  return {
    key: `move:${m.key}`, number: m.evidence.find((e) => e.number)?.number ?? null, href, title: m.title, sub: firstSentence(m.why), area: a.area, appName: a.appName,
    cells: [
      <Link to={href}>{m.action.label}</Link>,
      m.owner?.id ? (m.owner.id === meId ? <b>You</b> : m.owner.name ?? "–") : <span className="muted">Nobody yet</span>,
      <span className="muted">{m.when}</span>,
    ],
  };
}

const AREA_ORDER = new Map(SUITE.map((d, i) => [d.id, i]));
const Chev = ({ open }: { open: boolean }) => <svg className={`mw-chev ${open ? "open" : ""}`} width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M3.5 2 6.5 5l-3 3" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>;

/** A section's rows in one grid, grouped by area and then by app, each group with its count and folding. */
function GroupedGrid({ sid, cols, lines, folded, fold }: { sid: string; cols: Col[]; lines: Line[]; folded: Set<string>; fold: (k: string) => void }) {
  const groups = useMemo(() => {
    const byArea = new Map<string, Map<string, Line[]>>();
    for (const l of lines) {
      const apps = byArea.get(l.area) ?? byArea.set(l.area, new Map()).get(l.area)!;
      (apps.get(l.appName) ?? apps.set(l.appName, []).get(l.appName)!).push(l);
    }
    return [...byArea.entries()].sort((a, b) => (AREA_ORDER.get(a[0]) ?? 99) - (AREA_ORDER.get(b[0]) ?? 99) || areaName(a[0]).localeCompare(areaName(b[0])))
      .map(([area, apps]) => ({ area, n: [...apps.values()].reduce((t, x) => t + x.length, 0), apps: [...apps.entries()].sort((a, b) => a[0].localeCompare(b[0])) }));
  }, [lines]);
  const span = 2 + cols.length;
  return (
    <div className="table-wrap"><table className="dgrid mw-grid">
      <thead><tr><th>Number</th><th>Title</th>{cols.map((c) => <th key={c.label} className={c.hideM ? "mw-hide-m" : ""}>{c.label}</th>)}</tr></thead>
      {groups.map((g) => {
        const ak = `${sid}|${g.area}`, aOpen = !folded.has(ak);
        return (
          <tbody key={g.area}>
            <tr className="mw-g1"><td colSpan={span}><button aria-expanded={aOpen} onClick={() => fold(ak)}><Chev open={aOpen} />{areaName(g.area)}<span className="n">{g.n}</span></button></td></tr>
            {aOpen && g.apps.map(([app, rows]) => {
              const pk = `${ak}|${app}`, pOpen = !folded.has(pk);
              return [
                <tr key={pk} className="mw-g2"><td colSpan={span}><button aria-expanded={pOpen} onClick={() => fold(pk)}><Chev open={pOpen} />{app}<span className="n">{rows.length}</span></button></td></tr>,
                ...(pOpen ? rows.map((l) => (
                  <tr key={l.key}>
                    <td className="mw-numc"><Link className="num" to={l.href}>{l.number ?? "Open"}</Link></td>
                    <td className="title" title={l.title}>{l.title}{l.sub && <span className="sub">{l.sub}</span>}</td>
                    {l.cells.map((c, i) => <td key={i} className={cols[i]?.hideM ? "mw-hide-m" : ""} style={{ whiteSpace: "nowrap" }}>{c}</td>)}
                  </tr>)) : []),
              ];
            })}
          </tbody>
        );
      })}
    </table></div>
  );
}

const EMPTY: Record<View, string> = {
  all: "Nothing needs you right now.",
  waiting: "Nothing is waiting on you. Approvals, signatures and decisions from every app show here.",
  asked: "Nothing names you as the approver or signer right now.",
  ready: "",
  progress: "You don't own anything open right now.",
  overdue: "Nothing you own is overdue.",
  done: "Nothing finished in the last seven days yet.",
};

export function MyWorkPage() {
  const [params, setParams] = useSearchParams();
  const view = (VIEWS.find((v) => v.id === params.get("v"))?.id ?? "all") as View;
  const setView = (v: View) => setParams(v === "all" ? {} : { v }, { replace: true });
  const q = useLoad(() => myWork.all());
  const meId = useAccess()?.person.id ?? null;
  const [moves, setMoves] = useState<Move[] | null>(null);
  const [text, setText] = useState("");
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const fold = (k: string) => setFolded((f) => { const n = new Set(f); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const refresh = () => { q.reload(); workChanged(); };
  // Coming back to the tab: load again, so what someone else approved meanwhile drops off.
  useEffect(() => {
    const on = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const d = q.data;
  const today = todayIso();
  const needle = text.trim().toLowerCase();
  const match = (l: Line) => !needle || [l.number, l.title, l.sub, l.appName, areaName(l.area)].some((s) => s?.toLowerCase().includes(needle));
  const lines: Record<Section, Line[]> = useMemo(() => ({
    waiting: (d?.waiting ?? []).map((w) => itemLine(w, "waiting")),
    ready: (moves ?? []).map((m) => moveLine(m, meId)),
    progress: (d?.progress ?? []).map((w) => itemLine(w, "progress")),
    done: (d?.done ?? []).map((w) => itemLine(w, "done")),
  }), [d, moves, meId]);
  const counts: Record<View, number | null> = {
    all: null,
    waiting: d ? lines.waiting.length : null, asked: d ? lines.waiting.filter((l) => l.asked).length : null, ready: moves ? moves.length : null,
    progress: d ? lines.progress.length : null, overdue: d ? lines.progress.filter((l) => l.due && l.due < today).length : null, done: d ? lines.done.length : null,
  };
  // The sections this view shows, each with its rows.
  const shown: { sid: Section; title: string; rows: Line[] }[] = (view === "all" ? (["waiting", "ready", "progress", "done"] as Section[]).map((sid) => ({ sid, title: SECTION_NAME[sid], rows: lines[sid] }))
    : view === "asked" ? [{ sid: "waiting" as Section, title: "Asked of you", rows: lines.waiting.filter((l) => l.asked) }]
    : view === "overdue" ? [{ sid: "progress" as Section, title: "Overdue", rows: lines.progress.filter((l) => l.due && l.due < today) }]
    : view === "ready" ? [] : [{ sid: view, title: SECTION_NAME[view], rows: lines[view] }])
    .map((s) => ({ ...s, rows: s.rows.filter(match) }))
    .filter((s) => view !== "all" || s.rows.length);
  const areaKeys = [...new Set(shown.flatMap((s) => s.rows.map((l) => `${s.sid}|${l.area}`)))];
  const anyOpen = areaKeys.some((k) => !folded.has(k));

  return (
    <div className="page mw">
      <div className="page-title"><h1>My Work</h1><span className="sub">What's waiting on you and what you own, from every app</span></div>
      <ErrorBox error={q.error} />
      <div className="mw-desk">
        <nav className="mw-views" aria-label="Views">
          {VIEWS.map((v) => {
            const n = counts[v.id];
            return (
              <button key={v.id} aria-current={view === v.id} onClick={() => setView(v.id)} className={v.sub ? "in" : ""}>
                <span>{v.name}</span>{n !== null && <span className={`n ${v.id === "overdue" && n ? "hot" : ""}`}>{n}</span>}
              </button>
            );
          })}
        </nav>

        <div className="mw-main">
          {/* Ready for you stays mounted (hidden in other views): it's where steps are approved, and it feeds the rows. */}
          <div hidden={view !== "ready"}>
            <ReadyForYou mine sub="Next steps Courage found in your leads, accounts and Business Central sales, each one drafted. Nothing goes out until you approve it."
              onChanged={() => q.reload()} onMoves={setMoves} openKey={params.get("k")} active={view === "ready"} />
          </div>
          {view !== "ready" && (
            <>
              <div className="mw-bar">
                <input className="input" type="search" aria-label="Filter" placeholder="Filter by number, title, app or area" value={text} onChange={(e) => setText(e.target.value)} />
                <span className="spacer" />
                {areaKeys.length > 0 && <button className="btn" onClick={() => setFolded(anyOpen ? new Set(areaKeys) : new Set())}>{anyOpen ? "Collapse all" : "Expand all"}</button>}
                <button className="btn" onClick={refresh}>Refresh</button>
              </div>
              {!d ? <section className="card"><div className="empty">{q.error ? "" : "Loading…"}</div></section>
                : !shown.some((s) => s.rows.length) ? <section className="card"><div className="empty">{needle ? "Nothing matches." : EMPTY[view]}</div></section>
                : shown.map((s) => (
                  <section key={s.sid} className="mw-sec" aria-labelledby={`mw-${s.sid}`}>
                    <h2 id={`mw-${s.sid}`} className="mw-sec-h">{s.title}<span className="n">{s.rows.length}</span>
                      {s.sid === "ready" && <Link to="/my-work?v=ready" className="mw-sec-a">Approve, edit or skip them</Link>}</h2>
                    <div className="card"><GroupedGrid sid={s.sid} cols={COLS[s.sid]} lines={s.rows} folded={folded} fold={fold} /></div>
                  </section>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
